// Per-session breakdown of a team's scores in one category. With sessions, the same team can be
// scored on the same category by different panels; results show each panel's numbers side by side.

export interface ResultScorecard {
    ideaId: string;
    judgeVoteAverage: number;
    participantVoteAverage: number;
    judgeVotes?: Array<{ judge: string, score: number, session?: string | null }>;
    participantSessions?: Array<{ session: string | null, average: number, count: number }>;
}

export interface SessionScore {
    sessionId: string | null;
    judgeAverage: number;
    judgeCount: number;
    participantAverage: number;
    participantCount: number;
}

const mean = (values: number[]) => values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;

/**
 * One row per session that scored the team; judge scores of 0 are abstentions and don't count.
 */
export const sessionBreakdown = (card: ResultScorecard): SessionScore[] => {
    const rows = new Map<string, { sessionId: string | null, judge: number[], participantAverage: number, participantCount: number }>();
    const row = (sessionId: string | null) => {
        const key = sessionId || '';
        if (!rows.has(key)) {
            rows.set(key, { sessionId, judge: [], participantAverage: 0, participantCount: 0 });
        }
        return rows.get(key)!;
    };
    (card.judgeVotes || []).filter(v => v.score > 0).forEach(v => row(v.session || null).judge.push(v.score));
    (card.participantSessions || []).forEach(p => {
        const r = row(p.session || null);
        r.participantAverage = p.average;
        r.participantCount = p.count;
    });
    return Array.from(rows.values()).map(r => ({
        sessionId: r.sessionId,
        judgeAverage: mean(r.judge),
        judgeCount: r.judge.length,
        participantAverage: r.participantAverage,
        participantCount: r.participantCount
    }));
}

export const judgeCount = (card: ResultScorecard) => (card.judgeVotes || []).filter(v => v.score > 0).length;

/**
 * The team's scores counting only one session (for the session filter), or null if it wasn't scored there.
 */
export const scoreInSession = (card: ResultScorecard, sessionId: string): SessionScore | null => {
    return sessionBreakdown(card).find(r => r.sessionId === sessionId) || null;
}
